"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ShopifyClient = void 0;
const base_client_1 = require("@deliverr/base-client");
const client_auth_1 = require("@deliverr/client-auth");
const core_auth_1 = require("@deliverr/core-auth");
const ShopifyError_1 = require("./ShopifyError");
const commons_logs_1 = require("@deliverr/commons-logs");
class ShopifyClient extends base_client_1.ApiClient {
    constructor(config = {}) {
        const tokenHandler = new client_auth_1.OAuthClientTokenBuilder(config);
        super(Object.assign(Object.assign({}, config), { baseURL: `${config.baseURL || process.env.SHOPIFY_URL}/v1/`, authEngine: core_auth_1.AuthEngine.COGNITO_INTERNAL, tokenRefreshFn: tokenHandler.getTokenFn() }));
        if (!config.baseURL && !process.env.SHOPIFY_URL) {
            commons_logs_1.log.warn("SHOPIFY_URL environment variable is required or the baseURL must be provided in the config");
        }
    }
    async generateShopifyAuthUrl(sellerId, shop) {
        try {
            return await this.post(`/auth/seller/${sellerId}/shop/${shop}/url`, undefined);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, []);
        }
    }
    async registerSeller(sellerId, authParams) {
        try {
            return await this.post(`/auth/seller/${sellerId}`, authParams);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [
                ShopifyError_1.ShopifyError.ALREADY_HAS_SHOP,
                ShopifyError_1.ShopifyError.DUPLICATE_SELLER,
                ShopifyError_1.ShopifyError.INVALID_HMAC,
                ShopifyError_1.ShopifyError.INVALID_HOSTNAME,
                ShopifyError_1.ShopifyError.INVALID_NONCE,
                ShopifyError_1.ShopifyError.MISSING_SCOPES,
            ]);
        }
    }
    async activateSeller(sellerId, shop) {
        try {
            return await this.post(`/seller/${sellerId}/shop/${shop}/activate`, undefined);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [
                ShopifyError_1.ShopifyError.APP_NOT_INSTALLED,
                ShopifyError_1.ShopifyError.INELIGIBLE_SHOPIFY_ACCOUNT,
                ShopifyError_1.ShopifyError.NOT_FOUND,
            ]);
        }
    }
    async deactivateSeller(sellerId, shop) {
        try {
            return await this.delete(`/seller/${sellerId}/shop/${shop}`);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [ShopifyError_1.ShopifyError.NOT_FOUND]);
        }
    }
    async getSeller(sellerId) {
        try {
            return await this.get(`/seller/${sellerId}`);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [ShopifyError_1.ShopifyError.NOT_FOUND]);
        }
    }
    async updateSeller(sellerId, update) {
        try {
            return await this.put(`/seller/${sellerId}`, update);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [ShopifyError_1.ShopifyError.NOT_FOUND]);
        }
    }
    async refreshSeller(sellerId) {
        try {
            await this.post(`refresh/seller/${sellerId}`, undefined);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [
                ShopifyError_1.ShopifyError.NO_SHOP_FROM_CLIENT,
                ShopifyError_1.ShopifyError.NOT_FOUND,
                ShopifyError_1.ShopifyError.REFRESH_ALREADY_IN_PROGRESS,
                ShopifyError_1.ShopifyError.UNAUTHORIZED,
            ]);
        }
    }
    async getAllSellers(limit, offset, filters = {}) {
        try {
            return await this.get(`/sellers`, Object.assign({ limit,
                offset }, filters));
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, []);
        }
    }
    async getConfiguration(shop) {
        try {
            return await this.get(`/config/${shop}`);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [ShopifyError_1.ShopifyError.NOT_FOUND]);
        }
    }
    async updatePageConfiguration(sellerId, shop, pageType, config) {
        try {
            return await this.put(`/seller/${sellerId}/shop/${shop}/config/${pageType}`, config);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [ShopifyError_1.ShopifyError.NOT_FOUND]);
        }
    }
    async updateConfigurationForAllPages(sellerId, shop, config) {
        try {
            return await this.put(`/seller/${sellerId}/shop/${shop}/config`, config);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [ShopifyError_1.ShopifyError.NOT_FOUND]);
        }
    }
    async getHomepageLink(sellerId) {
        try {
            return await this.get(`/seller/${sellerId}/homepage/link`);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [ShopifyError_1.ShopifyError.NOT_FOUND]);
        }
    }
    async getHomepageLinks(sellerId) {
        try {
            return await this.get(`/seller/${sellerId}/homepage/links`);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [ShopifyError_1.ShopifyError.NOT_FOUND]);
        }
    }
    async getProductLinks(sellerId, shop, limit = 1) {
        try {
            if (shop) {
                return await this.get(`/seller/${sellerId}/shop/${shop}/products/links`, {
                    limit,
                });
            }
            return await this.get(`/seller/${sellerId}/products/links`, {
                limit,
            });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [ShopifyError_1.ShopifyError.NOT_FOUND]);
        }
    }
    async getLinkForDsku(dsku) {
        try {
            return await this.get(`/dsku/${dsku}/link`);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [ShopifyError_1.ShopifyError.NOT_FOUND, ShopifyError_1.ShopifyError.NOT_FOUND_IN_MARKETPLACE]);
        }
    }
    async getDskuStateCoverage(dsku) {
        try {
            return await this.get(`/dsku/${dsku}/coverage/twoday`);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, []);
        }
    }
    async getStateCoverageByServiceLevel(dsku) {
        try {
            return await this.get(`/coverage/servicelevel/${dsku}`);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, []);
        }
    }
    async getShopifyVariant(sellerId, shopifyVariantId) {
        try {
            return await this.get(`/seller/${sellerId}/variant/${shopifyVariantId}`);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [ShopifyError_1.ShopifyError.NOT_FOUND]);
        }
    }
    async getShopifyVariantByDsku(sellerId, dsku) {
        try {
            return await this.get(`/seller/${sellerId}/dsku/${dsku}`);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [ShopifyError_1.ShopifyError.NOT_FOUND]);
        }
    }
    async getShopifyVariantsByDsku(sellerId, dskus) {
        try {
            return await this.get(`/seller/${sellerId}/variants`, {
                dskus,
            });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, []);
        }
    }
    async getShopifyVariants(sellerId, ids, dskus, shop, activeShopsOnly, limit, offset) {
        try {
            return await this.get(`/seller/${sellerId}/variants`, {
                ids,
                dskus,
                activeShopsOnly,
                shop,
                limit,
                offset,
            });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, []);
        }
    }
    async getDefaultShopifyVariantIds(sellerId, productIds, shop) {
        try {
            if (shop) {
                return await this.get(`seller/${sellerId}/shop/${shop}/products/default-variants`, {
                    ids: productIds,
                });
            }
            return await this.get(`seller/${sellerId}/products/default-variants`, {
                ids: productIds,
            });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, []);
        }
    }
    async getShopifyProducts(sellerId, ids, createdSince, activeShopsOnly, shop, limit, offset) {
        try {
            return await this.get(`/seller/${sellerId}/products`, {
                ids,
                createdSince,
                activeShopsOnly,
                shop,
                limit,
                offset,
            });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, []);
        }
    }
    async getSellerConnectionStatus(sellerId) {
        try {
            return await this.get(`/seller/${sellerId}/connectionstatus`);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, []);
        }
    }
    async isFastTagEnabled(sellerId, dsku) {
        try {
            return await this.get(`/seller/${sellerId}/dsku/${dsku}/active`);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, []);
        }
    }
    async enableFastTag(sellerId, dsku) {
        try {
            await this.put(`/seller/${sellerId}/fast-tag/enabled`, [dsku]);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, []);
        }
    }
    async disableFastTag(sellerId, dsku) {
        try {
            await this.put(`/seller/${sellerId}/fast-tag/disabled`, [dsku]);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, []);
        }
    }
    async updateLinkForDsku(sellerId, dsku, listingUrl, productIdentifier, shop) {
        try {
            return await this.put(`/seller/${sellerId}/dsku/${dsku}/variant`, {
                shopifyVariantUrl: listingUrl,
                shopifyVariantId: productIdentifier,
                shop,
            });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [ShopifyError_1.ShopifyError.INVALID_URL, ShopifyError_1.ShopifyError.MULTIPLE_VARIANTS, ShopifyError_1.ShopifyError.NOT_FOUND]);
        }
    }
    async getWarehousesByDsku(sellerId, dskus) {
        try {
            return await this.get(`/seller/${sellerId}/coverage/warehouses`, { dskus });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, []);
        }
    }
    async getWarehouseCombinationCoverage(warehouseIds) {
        try {
            return await this.get(`/warehouses/combined-coverage`, { warehouseIds });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, []);
        }
    }
    async getNationwideCoverageThreshold() {
        try {
            return await this.get("/coverage/nationwide-threshold");
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async getDskuListings(sellerId, dsku, shop) {
        try {
            return await this.get(`/seller/${sellerId}/dsku/${dsku}/listings`, {
                shop,
            });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [ShopifyError_1.ShopifyError.NOT_FOUND, ShopifyError_1.ShopifyError.NOT_FOUND_IN_MARKETPLACE]);
        }
    }
    async getStoreConfiguration(sellerId, storeName) {
        try {
            return await this.get(`/seller/${sellerId}/store/${storeName}/configuration`);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [ShopifyError_1.ShopifyError.NOT_FOUND]);
        }
    }
    async setStorePrices(sellerId, storeName, cartMinimum, paidFastTagCost) {
        try {
            return await this.put(`/seller/${sellerId}/store/${storeName}/prices`, {
                cartMinimum,
                paidFastTagCost,
            });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, []);
        }
    }
    async setStoreStandardBadgeStatus(sellerId, storeName = sellerId, standardBadgeStatus) {
        try {
            return await this.put(`/seller/${sellerId}/store/${storeName}/standard`, {
                standardBadgeStatus,
            });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, []);
        }
    }
    async setStoreShippingSetting(sellerId, storeName = sellerId, settingName, settingData) {
        try {
            return await this.put(`/seller/${sellerId}/store/${storeName}/shipping-setting/${settingName}`, settingData);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, []);
        }
    }
    async getTemplatesWithAppBlockSupport(sellerId, shop) {
        try {
            return await this.get(`/seller/${sellerId}/shop/${shop}/themes/templates/app-block-support`);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [ShopifyError_1.ShopifyError.INVALID_MAIN_ASSET, ShopifyError_1.ShopifyError.INVALID_TEMPLATE_ASSET]);
        }
    }
    async isDeliverrAppEmbedEnabledForActiveTheme(sellerId, shop) {
        try {
            return await this.get(`/seller/${sellerId}/shop/${shop}/themes/templates/deliverr-app-embed`);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [ShopifyError_1.ShopifyError.NOT_FOUND, ShopifyError_1.ShopifyError.INVALID_SETTINGS_ASSET]);
        }
    }
    async removeStorefrontScript(sellerId, shop) {
        try {
            return await this.delete(`/seller/${sellerId}/shop/${shop}/script`);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [ShopifyError_1.ShopifyError.UNAUTHORIZED]);
        }
    }
    async getShopFastTagAppInstallationStatus(sellerId, shop) {
        try {
            return await this.get(`/seller/${sellerId}/shop/${shop}/app-installed`);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, []);
        }
    }
    async getShippingRateOffer(requestHash, primaryHashKey, shippingRateName) {
        try {
            return await this.get("/shipping-rate-offer", {
                requestHash,
                primaryHashKey,
                shippingRateName,
            });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [ShopifyError_1.ShopifyError.NOT_FOUND]);
        }
    }
}
exports.ShopifyClient = ShopifyClient;
//# sourceMappingURL=ShopifyClient.js.map